export const LOCATION_SEARCH_TYPES = [
  'address',
  'road',
  'postal_code',
  'place',
  'locality',
  'municipality',
  'joint_municipality',
  'county'
]

const ZOOM_BY_TYPE = {
  address: 15,
  road: 14,
  postal_code: 12,
  place: 11,
  locality: 11,
  municipality: 10.5,
  joint_municipality: 10,
  county: 8.5
}

const DEFAULT_ZOOM = 10
const GPS_COORDINATE_ZOOM = 15

const COORDINATE_TOKEN_PATTERN = /([NSEW])?\s*([+-]?(?:\d+(?:\.\d*)?|\.\d+))\s*(?:°|deg(?:rees)?)?\s*([NSEW])?/gi
const GPS_LABEL_PATTERN = /\b(?:coord(?:inate)?s?|gps|lat(?:itude)?|lng|lon(?:gitude)?|long)\b/gi

export const locationFeatureLabel = (feature) => feature?.place_name || feature?.text || 'Location'

export const locationFeatureType = (feature) => {
  const types = Array.isArray(feature?.place_type) ? feature.place_type : []
  return types[0] || ''
}

export const zoomForLocationFeature = (feature) => ZOOM_BY_TYPE[locationFeatureType(feature)] || DEFAULT_ZOOM

export const locationFeatureCenter = (feature) => {
  if (validCoordinatePair(feature?.center)) return feature.center

  const geometryCoordinates = feature?.geometry?.type === 'Point' ? feature.geometry.coordinates : null
  if (validCoordinatePair(geometryCoordinates)) return geometryCoordinates

  return null
}

export const locationFeatureBounds = (feature) => {
  const bbox = feature?.bbox
  if (!Array.isArray(bbox) || bbox.length !== 4) return null

  const coordinates = bbox.map((coordinate) => Number(coordinate))
  if (!coordinates.every(Number.isFinite)) return null

  const [west, south, east, north] = coordinates
  if (east <= west || north <= south) return null

  return [[west, south], [east, north]]
}

export const cameraForLocationFeature = (feature, { padding = 92, duration = 500 } = {}) => {
  const center = locationFeatureCenter(feature)
  if (!center) return null

  const zoom = zoomForLocationFeature(feature)
  const bounds = locationFeatureBounds(feature)

  if (bounds) {
    return {
      kind: 'bounds',
      bounds,
      fitBoundsOptions: { padding, maxZoom: zoom, duration }
    }
  }

  return {
    kind: 'center',
    center,
    easeOptions: { center, zoom, duration }
  }
}

export const parseGpsCoordinateQuery = (query) => {
  const normalizedQuery = String(query || '').trim()
  if (!normalizedQuery) return null

  const tokens = Array.from(normalizedQuery.matchAll(COORDINATE_TOKEN_PATTERN), coordinateTokenFromMatch)
  if (tokens.length !== 2 || hasUnsupportedGpsText(normalizedQuery)) return null

  const coordinates = coordinatesFromTokens(tokens)
  if (!coordinates) return null

  return {
    ...coordinates,
    center: [coordinates.longitude, coordinates.latitude],
    label: `GPS ${formatCoordinate(coordinates.latitude)}, ${formatCoordinate(coordinates.longitude)}`
  }
}

export const cameraForGpsCoordinates = (coordinates, { zoom = GPS_COORDINATE_ZOOM, duration = 500 } = {}) => {
  if (!validLatitude(coordinates?.latitude) || !validLongitude(coordinates?.longitude)) return null

  const center = [coordinates.longitude, coordinates.latitude]
  return {
    kind: 'center',
    center,
    easeOptions: { center, zoom, duration }
  }
}

const validCoordinatePair = (coordinates) => {
  if (!Array.isArray(coordinates) || coordinates.length !== 2) return false

  return coordinates.every((coordinate) => Number.isFinite(Number(coordinate)))
}

const coordinateTokenFromMatch = (match) => {
  const direction = normalizedDirection(match[1] || match[3])
  const numericValue = Number(match[2])

  return {
    direction,
    value: direction ? directionalValue(numericValue, direction) : numericValue
  }
}

const coordinatesFromTokens = ([first, second]) => {
  const firstAxis = axisForDirection(first.direction)
  const secondAxis = axisForDirection(second.direction)

  if (firstAxis && secondAxis) {
    if (firstAxis === secondAxis) return null

    return buildCoordinates(
      firstAxis === 'latitude' ? first.value : second.value,
      firstAxis === 'longitude' ? first.value : second.value
    )
  }

  if (firstAxis === 'latitude') return buildCoordinates(first.value, second.value)
  if (firstAxis === 'longitude') return buildCoordinates(second.value, first.value)
  if (secondAxis === 'latitude') return buildCoordinates(second.value, first.value)
  if (secondAxis === 'longitude') return buildCoordinates(first.value, second.value)

  const latitudeFirstCoordinates = buildCoordinates(first.value, second.value)
  if (latitudeFirstCoordinates) return latitudeFirstCoordinates

  return buildCoordinates(second.value, first.value)
}

const buildCoordinates = (latitude, longitude) => {
  if (!validLatitude(latitude) || !validLongitude(longitude)) return null

  return { latitude, longitude }
}

const hasUnsupportedGpsText = (query) => {
  const residualText = query
    .replace(COORDINATE_TOKEN_PATTERN, ' ')
    .replace(GPS_LABEL_PATTERN, ' ')
    .replace(/[,\s;:/|()[\]{}=°-]/g, '')

  return residualText.length > 0
}

const axisForDirection = (direction) => {
  if (direction === 'N' || direction === 'S') return 'latitude'
  if (direction === 'E' || direction === 'W') return 'longitude'

  return null
}

const directionalValue = (value, direction) => {
  const magnitude = Math.abs(value)
  return direction === 'S' || direction === 'W' ? -magnitude : magnitude
}

const normalizedDirection = (direction) => {
  const normalized = String(direction || '').toUpperCase()
  return ['N', 'S', 'E', 'W'].includes(normalized) ? normalized : ''
}

const validLatitude = (value) => Number.isFinite(value) && value >= -90 && value <= 90

const validLongitude = (value) => Number.isFinite(value) && value >= -180 && value <= 180

const formatCoordinate = (value) => Number(value.toFixed(6)).toString()
